import { OpenGovAppConfig } from '@opengov/app-config';
import _ from 'lodash';

// Vite: ?inline returns compiled CSS as a string without injecting into <head>.
import ieEditorStyles from '../styles/ie-editor-styles.css?inline';

import { ContainerData, ContainerTypes, INewStoryContent, IStoryContent, IStoryMargins, ProgressTrackerSettingType } from '../types';

import {
  CONTAINERS,
  DEFAULT_STORY_MARGINS,
  FONTS,
  GALLERY,
  GOOGLE_FONTS,
  OPENGOV_LOGO_URL,
  TINYMCE_GOOGLE_FONTS
} from '../constants';
import { IS_IE } from '../utils/environment';
import { getCustomFontString } from '../utils/fonts';
import { b64EncodeUnicode } from '../utils/general';
import { createContainer } from './progressTracker';

const operateUrl = OpenGovAppConfig.OPERATE_URL;
const embedBundleURL = OpenGovAppConfig.EMBED_BUNDLE_URL;
const storyContentStylesURL = OpenGovAppConfig.MODULE_STYLES_URL;
const propertyId = OpenGovAppConfig.GOOGLE_ANALYTICS_PROPERTY_ID;
const storiesUrl = OpenGovAppConfig.STORIES_URL;
const storiesCommunityUrl = OpenGovAppConfig.STORIES_COMMUNITY_URL;
const storiesCommunityApiUrl = OpenGovAppConfig.STORIES_COMMUNITY_API_URL;
const pixelScaleFactor = 96; // 96px/in

export const setupPdfPageBreakStyles = `<style data-test="export-from-setup-pdf">
  @media print {
      .section-toolbar { display: none !important; }
      section.setup-pdf-page-break {
      break-before: page;
      page-break-before: always;
    }
  }
</style>`;
function inchesToPixels(inches: number): string {
  return (inches * pixelScaleFactor).toFixed(2);
}

// When a scale value is set in Puppeteer it seems to changing the scale of horizonal pixel values as well.
// The 1.81 multiplier scales the pixel values back to regular size. Determined this value by measuring the percent
// increase between 96px in the vertical direction and 96px in the horizontal direction.
function inchesToPixelsHorizontalScaleAdjust(inches: number): string {
  return (inches * pixelScaleFactor * 1.81).toFixed(2);
}

function stringifyStyles(stylesObject: { [key: string]: string | null }): string {
  return _(stylesObject)
    .toPairs()
    .map(([key, value]) => (value ? `${key}: ${value};` : null))
    .compact()
    .join('\n');
}

export const storyHasGallery = (storyContents) => storyContents.find((storyContent) => storyContent.group === GALLERY);

/**
 * Removes tabindex="0" from non-interactive elements for published/read-only mode.
 * These tabindex attributes are used in edit mode for selection but cause accessibility
 * issues in published versions where non-interactive elements shouldn't be focusable.
 */
export function removeEditModeTabindex(html: string): string {
  // List of non-interactive HTML elements that should not have tabindex in published mode
  const nonInteractiveTags = [
    'div',
    'header',
    'nav',
    'section',
    'article',
    'main',
    'aside',
    'footer',
    'p',
    'span',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'figure',
    'figcaption',
    'blockquote',
    'pre',
    'code'
  ];

  // Create a regex pattern that matches these tags with tabindex="0"
  // This pattern captures the opening tag content before tabindex and removes tabindex="0"
  const tagPattern = nonInteractiveTags.join('|');
  const regex = new RegExp(
    `(<(?:${tagPattern})(?:\\s+[^>]*?)?)\\s+tabindex=["']0["']((?:\\s+[^>]*)?>)`,
    'gi'
  );

  return html.replace(regex, '$1$2');
}

export function calculateMargins(savedMargins: IStoryMargins, hasFootNotes: boolean): IStoryMargins {
  // If footnotes are displayed, we need to ensure there is enough space for the footnotes
  // to be visible by alway rendering an additional 0.25" bottom margin
  const marginBottom = savedMargins.marginBottom ?? DEFAULT_STORY_MARGINS.marginBottom;
  const marginBottomWithFootNotes = hasFootNotes ? DEFAULT_STORY_MARGINS.marginBottom + marginBottom : marginBottom;

  return {
    marginTop: savedMargins.marginTop ?? DEFAULT_STORY_MARGINS.marginTop,
    marginBottom: marginBottomWithFootNotes ?? DEFAULT_STORY_MARGINS.marginBottom,
    marginLeft: savedMargins.marginLeft ?? DEFAULT_STORY_MARGINS.marginLeft,
    marginRight: savedMargins.marginRight ?? DEFAULT_STORY_MARGINS.marginRight
  };
}

export function generateMarginStyles(
  storyMarginsEnabled: boolean,
  savedMargins: IStoryMargins,
  hasFootNotes: boolean,
  /**
   * When Enlarge Fonts is on, keep print zoom on embedded/report tables but
   * reset it for rich-text (`div.editable`) tables so CES font enlargement
   * does not double-stack (BUD-18283 / CRD-13309 vs CRD-12047).
   */
  enlargeFontsForPdf = false
): string {
  if (!storyMarginsEnabled) {
    return '';
  }

  const margins = calculateMargins(savedMargins, hasFootNotes);

  const pageStyles = stringifyStyles({
    'margin-top': !_.isNil(margins.marginTop) ? `${inchesToPixels(margins.marginTop)}px !important` : null,
    'margin-bottom': !_.isNil(margins.marginBottom) ? `${inchesToPixels(margins.marginBottom)}px !important` : null
  });

  // Content should be centered unless custom margins are specified
  const containerMaxWidth = _.isNil(margins.marginLeft) && _.isNil(margins.marginRight) ? '1220px' : 'none !important';

  const sectionStyles = stringifyStyles({
    'padding-left': !_.isNil(margins.marginLeft)
      ? `${inchesToPixelsHorizontalScaleAdjust(margins.marginLeft)}px !important`
      : null,
    'padding-right': !_.isNil(margins.marginRight)
      ? `${inchesToPixelsHorizontalScaleAdjust(margins.marginRight)}px !important`
      : null
  });

  // BUD-8587: baseline print zoom for table/editor parity.
  // With Enlarge Fonts, CES enlarges div.editable td (and similar) but not
  // embedded report tables — so keep zoom on tables generally, and cancel it
  // inside div.editable to avoid CRD-12047 stacking while fixing CRD-13309.
  const printTableZoomBlock = enlargeFontsForPdf
    ? `table {
        zoom: 1.2;
      }
      div.editable table {
        zoom: 1;
      }`
    : `table {
        zoom: 1.2;
      }`;

  return `<style data-test="margin-print-styles">
    @page {
      ${pageStyles}
    }

    @media print {
      header, section {
        ${sectionStyles}
      }
      ${printTableZoomBlock}
      .container,
      .full-width-content,
      .media-block {
        max-width: ${containerMaxWidth};
      }
    }
  </style>`;
}

export const CUSTOM_CSS_SCOPE_PREFIX = 'custom_css_scope';
export const getStoryCustomCssScopeId = (id?: string) => `story${id ? `_${id}` : ''}_${CUSTOM_CSS_SCOPE_PREFIX}`;

export const normalizeCss = (css: string): string => {
  return css
    .replace(/\{\s*/g, `{\n `) // Add newline after opening brace
    .replace(/\}\s*/g, '\n}\n') // Add newline after closing brace
    .replace(/\n\n/g, '\n') // Remove double newlines
    .trim();
};
export const applyCustomCssToScope = (customCss: string, scopeSelector: string): string => {
  // Normalize css to ensure each rule/css block is on a new line
  const normalizedCss = normalizeCss(customCss);
  // Include newlines so comma-separated selector lists that wrap still get scoped.
  return normalizedCss.replace(/(^|\n)(\s*)([^\{\}]+?)\s*\{/g, (match, newline, spaces, selectorGroup) => {
    // Ignore @media, @keyframes, @supports etc.
    if (selectorGroup.trim().startsWith('@')) return match;

    // Handle multiple selectors (e.g., ".a, .b" or the same list split across lines)
    const scopedSelectors = selectorGroup
      .split(',')
      .map((selector) => {
        const trimmed = selector.trim();
        // Don't modify keyframe selectors or global tags
        if (/^(from|to|\d+%|@)/.test(trimmed)) return trimmed;
        // Prefix only class or element selectors
        return `${scopeSelector} ${trimmed}`;
      })
      .join(', ');

    return `${newline}${scopedSelectors} {`;
  });
};

export function getFullPageContent(
  storyContents: (IStoryContent | INewStoryContent)[],
  isStoryWithProgressTracker?: boolean,
  entityId?: number,
  progressTrackerSettings?: ProgressTrackerSettingType[]
) {
  // Build page content string from storyContents

  const pageContents = [];
  let containers = [];
  let processedStoryContents = storyContents;

  if (isStoryWithProgressTracker) {
    // Process all story contents, updating those with progress trackers
    const { updatedStoryContents, containers: updatedContainers } = createContainer(storyContents, entityId, progressTrackerSettings);
    processedStoryContents = updatedStoryContents;
    containers = updatedContainers;
  }

  _.forEach(processedStoryContents, (storyContent) => {
    if (storyContent.active) {
      pageContents.push(storyContent.content);
    }
  });
  return { content: pageContents.join(''), containers };
}
// Generates the HTML used when requesting the Stories backend
export function generateBase64HTML({
  storyContents,
  title,
  description,
  hasGallery,
  hasFootNotes,
  thumbnail,
  thumbnail_relative_path,
  fonts,
  options = {},
  margins,
  storyMarginsEnabled,
  id = '',
  storyCustomCss,
  publicationCustomCss,
  isFollowable,
  entityUuid,
  isExportFromSetupPdf,
  entityId = undefined,
  progressTrackerSettings = [],
  enlargeFontsForPdf = false
}: {
  storyContents: (IStoryContent | INewStoryContent)[];
  title: string;
  description: string;
  hasGallery: boolean;
  hasFootNotes: boolean;
  thumbnail?: string;
  thumbnail_relative_path?: string;
  fonts: {
    headline: string;
    paragraph: string;
  };
  options?: {
    pdf?: boolean;
    gaTrackingCode?: string;
    showSocialButtons?: boolean;
    noJS?: boolean;
  };
  margins?: IStoryMargins;
  storyMarginsEnabled?: boolean;
  id?: string;
  storyCustomCss?: string;
  publicationCustomCss?: string;
  isFollowable?: boolean;
  entityUuid?: string;
  isExportFromSetupPdf?: boolean;
  entityId?: number;
  progressTrackerSettings?: ProgressTrackerSettingType[];
  enlargeFontsForPdf?: boolean;
}) {
  const isStoryWithProgressTracker = storyContents.some((storyContent) => storyContent.contentChartConfig?.length > 0);
  const { content, containers } = getFullPageContent(storyContents, isStoryWithProgressTracker, entityId, progressTrackerSettings);
  // Generate page HTML file
  const html = generateHTML({
    title,
    description,
    fonts: [fonts],
    content,
    hasGallery,
    hasFootNotes,
    thumbnail,
    thumbnail_relative_path,
    options,
    margins,
    storyMarginsEnabled,
    id,
    storyCustomCss,
    publicationCustomCss,
    isFollowable,
    containers,
    isStoryWithProgressTracker,
    entityUuid,
    isExportFromSetupPdf,
    enlargeFontsForPdf
  });
  // Convert HTML string to base 64
  return b64EncodeUnicode(html);
}

// Generates the HTML used when rendering an iframe for Story editing
// tslint:disable-next-line:cyclomatic-complexity
export function generateHTML({
  title,
  description,
  content,
  hasGallery,
  hasFootNotes,
  thumbnail_relative_path,
  isReadOnly,
  isForCustomCssInspector,
  options,
  margins = DEFAULT_STORY_MARGINS,
  storyMarginsEnabled,
  id = '',
  storyCustomCss = '',
  publicationCustomCss = '',
  isFollowable,
  entityUuid,
  containers = [],
  isStoryWithProgressTracker = false,
  addIgnorePortalScript = false,
  isExportFromSetupPdf = false,
  enlargeFontsForPdf = false
}: {
  title: string;
  description: string;
  fonts: {
    headline: string;
    paragraph: string;
  }[];
  content?: string;
  hasGallery?: boolean;
  hasFootNotes?: boolean;
  thumbnail?: string;
  thumbnail_relative_path?: string;
  isReadOnly?: boolean;
  storyCustomCss?: string;
  publicationCustomCss?: string;
  isForCustomCssInspector?: boolean;
  options?: {
    pdf?: boolean;
    gaTrackingCode?: string;
    showSocialButtons?: boolean;
    noJS?: boolean;
  };
  margins?: IStoryMargins;
  storyMarginsEnabled?: boolean;
  id?: string;
  isFollowable?: boolean;
  containers?: ContainerData[];
  isStoryWithProgressTracker?: boolean;
  entityUuid?: string;
  addIgnorePortalScript?: boolean;
  isExportFromSetupPdf?: boolean;
  enlargeFontsForPdf?: boolean;
}) {
  let pageHead = '';
  let pageContent = '';
  if (isFollowable && (isReadOnly ?? true)) {
    containers.push({
      id: `${id}`,
      type: ContainerTypes.FOLLOW_CONTAINER,
      data: null
    });
  }
  if (hasGallery) {
    pageHead += `
      <link rel="stylesheet" type="text/css" href="https://cdn.jsdelivr.net/gh/kenwheeler/slick@1.8.1/slick/slick.css" />
      <link rel="stylesheet" type="text/css" href="https://cdn.jsdelivr.net/gh/kenwheeler/slick@1.8.1/slick/slick-theme.css" />
    `;
  }
  // tslint:enable max-line-length

  // Add in font face rules for self hosted fonts
  pageHead += getCustomFontString(storiesUrl);

  // Create the google fonts link tag from custom fonts array
  let fontFamilyString = '';

  const additinalFontFamilyString = TINYMCE_GOOGLE_FONTS.map((googleFont) => googleFont.name).join('|');
  pageHead += `
    <link href="https://fonts.googleapis.com/css?family=${additinalFontFamilyString}" rel="stylesheet" />
  `;
  fontFamilyString = FONTS.reduce((fontNames, font) => {
    return GOOGLE_FONTS.includes(font.id) ? [...fontNames, font.name] : fontNames;
  }, []).join('|');

  if (fontFamilyString) {
    pageHead += `
      <link href="https://fonts.googleapis.com/css?family=${fontFamilyString}" rel="stylesheet" />
    `;
  }

  const embedBundleContent = `
    <script src="${embedBundleURL}"></script>
    <script>
      function buildEmbed(id, selector) {
        OpenGovSDK.embed({
          embedVersion: 'v2',
          baseUrl: '${operateUrl}',
          embed: {
            id: id,
            type: 'dashboardTile',
          },
          targetNode: document.querySelector(selector),
          pdf: ${options.pdf}
        });
      }
    </script>
  `;

  if (content) {
    if (isExportFromSetupPdf) {
      pageHead += setupPdfPageBreakStyles;
    }
    if (isForCustomCssInspector) {
      pageHead += `
        <style>
          .highlight-element,
          .highlight-active-element {
            outline: 2px solid rgb(0, 149, 255) !important;
            box-shadow: inset 0 0 0 1px rgb(0, 149, 255) !important;
          }
        </style>
      `;
      pageContent += embedBundleContent;
    } else {
      if (!options.noJS) {
        if (!!propertyId) {
          pageHead += `
            <script async src="https://www.googletagmanager.com/gtag/js?id=${propertyId}"></script>
            <script>
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${propertyId}');
            </script>
          `;
          if (!!options.gaTrackingCode) {
            pageHead += `
              <script>
                gtag('config', '${options.gaTrackingCode}');
              </script>
             `;
          }
        } else {
          if (!!options.gaTrackingCode) {
            pageHead += `
              <script async src="https://www.googletagmanager.com/gtag/js?id=${options.gaTrackingCode}"></script>
              <script>
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());

                  gtag('config', '${options.gaTrackingCode}');
                </script>
             `;
          }
        }
      }

      pageHead += `
        <script src="https://cdnjs.cloudflare.com/ajax/libs/es5-shim/4.5.7/es5-shim.min.js"></script>
        <script src="https://cdnjs.cloudflare.com/ajax/libs/es5-shim/4.5.7/es5-sham.min.js"></script>
        <script src="https://cdnjs.cloudflare.com/ajax/libs/json3/3.3.2/json3.min.js"></script>
        <script src="https://cdnjs.cloudflare.com/ajax/libs/es6-shim/0.34.2/es6-shim.min.js"></script>
        <script src="https://cdnjs.cloudflare.com/ajax/libs/es6-shim/0.34.2/es6-sham.min.js"></script>
      `;

      pageContent += `
        <script src="${embedBundleURL}"></script>
      `;

      if (storyContentStylesURL) {
        pageHead += `<link rel="stylesheet" type="text/css" href="${storyContentStylesURL}" ${!options.pdf && 'media="screen"'
          } />`;
      }
    }

    // Main story content is wrapped in a landmark. The social share buttons
    // are considered part of the main content, and the powered-by block is
    // exposed as a footer/contentinfo landmark for individual story pages.
    // Remove tabindex from non-interactive elements for accessibility in published mode
    const processedContent = removeEditModeTabindex(content);
    pageContent += `
      <main role="main">
        ${processedContent}
    `;

    if (options.showSocialButtons) {
      pageContent += `
        <div class="social-wrapper">
          <a class="facebook-share" href="#" aria-label="Share this story on facebook">
            <div class="icon-facebook-1"></div>
          </a>
          <a class="twitter-share" href="#" aria-label="Share this story on x">
            <div class="icon-twitter-1"></div>
          </a>
        </div>
      `;
    }

    pageContent += `
        <div class="powered-by">
          <a href="https://opengov.com" target="_blank" rel="noopener noreferrer">
            Powered by OpenGov
          </a>
        </div>
      </main>
    
    `;
  } else {
    // These styles should only be applied when it edit mode, not preview
    if (!isReadOnly && IS_IE) {
      // IE11 compatibility fixes for contenteditable
      pageContent += `<style>${ieEditorStyles}</style>`;
    } else if (!isReadOnly) {
      // This breaks contenteditable in IE11 but is required for other
      // browsers to display a caret (blinker) when a text area is empty
      pageContent += `
        <style>
          .editable {
            min-width: 1px;
          }
        </style>
      `;
    }
    pageContent += `
      <div id="wrapper"></div>
      ${embedBundleContent}
      <script>
        (function (ElementProto) {
          if (typeof ElementProto.matches !== 'function') {
            ElementProto.matches = ElementProto.msMatchesSelector ||
              ElementProto.mozMatchesSelector ||
              ElementProto.webkitMatchesSelector ||
              function matches(selector) {
                var element = this;
                var elements = (element.document || element.ownerDocument).querySelectorAll(selector);
                var index = 0;
                while (elements[index] && elements[index] !== element) {
                  ++index;
                }
                return Boolean(elements[index]);
              };
          }
          if (typeof ElementProto.closest !== 'function') {
            ElementProto.closest = function closest(selector) {
              var element = this;
              while (element && element.nodeType === 1) {
                if (element.matches(selector)) {
                  return element;
                }
                element = element.parentNode;
              }
              return null;
            };
          }
        })(window.Element.prototype);
      </script>
    `;
  }

  // tslint:disable max-line-length
  if (hasGallery) {
    // tslint:disable max-line-length
    pageContent += `
      <script type="text/javascript" src="https://cdn.jsdelivr.net/npm/jquery@1.12.4/dist/jquery.min.js"></script>
      <script type="text/javascript" src="https://cdn.jsdelivr.net/gh/kenwheeler/slick@1.8.1/slick/slick.min.js"></script>
      <script>
        function buildGallery() {
          $('.slick-gallery').not('.slick-initialized').slick({
            speed: 750,
            infinite: false,
            mobileFirst: true,
            responsive: [{
              breakpoint: 992,
              settings: {
                slidesToShow: 3,
              }
            }, {
              breakpoint: 768,
              settings: {
                slidesToShow: 2,
              }
            }]
          });
        }
        ${content
        ? `
          $(document).ready(buildGallery);
        `
        : ''
      }
      </script>
    `;
    // tslint:enable max-line-length
  }

  if (options.showSocialButtons) {
    let facebookShareTracker = ``;
    let twitterShareTracker = ``;

    const trackingCodes = [options.gaTrackingCode, propertyId].filter(Boolean);

    if (trackingCodes.length > 0) {
      const sendToValue =
        trackingCodes.length > 1 ? trackingCodes.map((code) => `'${code}'`).join(', ') : `'${trackingCodes[0]}'`;
      facebookShareTracker = `
        gtag('event', 'facebook', {
           'send_to': [${sendToValue}],
           'event_category': 'social-share',
           'event_label': 'ShareOnFacebook'
          });`;

      twitterShareTracker = `
        gtag('event', 'twitter', {
          'send_to': [${sendToValue}],
          'event_category': 'social-share',
          'event_label': 'ShareOnTwitter'
        });`;
    }

    pageContent += `
      <script>
        var getWindowOptions = function() {
          var width = 500;
          var height = 350;
          var left = (window.innerWidth / 2) - (width / 2);
          var top = (window.innerHeight / 2) - (height / 2);

          return [
            'resizable,scrollbars,status',
            'height=' + height,
            'width=' + width,
            'left=' + left,
            'top=' + top,
          ].join();
        };

        var facebookBtn = document.querySelector('.facebook-share');
        if (facebookBtn) {
          var facebookShareUrl = 'http://www.facebook.com/share.php?u=' + location.href;
          facebookBtn.href = facebookShareUrl;
          facebookBtn.addEventListener('click', function(e) {
            e.preventDefault();
            var win = window.open(facebookShareUrl, 'ShareOnFacebook', getWindowOptions());
            win.opener = null;
            ${facebookShareTracker}
          });
        }

        var twitterBtn = document.querySelector('.twitter-share');
        if (twitterBtn) {
          var twitterShareUrl = 'https://x.com/intent/post?url=' + location.href;
          twitterBtn.href = twitterShareUrl;
          twitterBtn.addEventListener('click', function(e) {
            e.preventDefault();
            var win = window.open(twitterShareUrl, 'ShareOnTwitter', getWindowOptions());
            win.opener = null;
            ${twitterShareTracker}
          });
        }
      </script>
    `;
  }

  pageHead += generateMarginStyles(storyMarginsEnabled, margins, hasFootNotes, enlargeFontsForPdf);

  const scopeId = getStoryCustomCssScopeId();
  const scopeSelector = `#${scopeId}`;
  if (publicationCustomCss) {
    pageHead += `<style data-test="story-publication-custom-css">${applyCustomCssToScope(
      publicationCustomCss,
      scopeSelector
    )}</style>`;
  }
  if (storyCustomCss) {
    pageHead += `<style data-test="story-custom-css">${applyCustomCssToScope(storyCustomCss, scopeSelector)}</style>`;
  }

  // Add script for followable stories (non-PDF, read-only)
  const shouldAddScriptForFollowable = isFollowable && !options.pdf && (isReadOnly == undefined || isReadOnly);

  if (shouldAddScriptForFollowable || isStoryWithProgressTracker) {
    if (addIgnorePortalScript) {
      pageContent += `
        <script>
          // This script is used to ignore the removeChild error that is thrown when the story is rendered in the iframe
          // This is a workaround to avoid the error that is thrown when the story is rendered in the iframe and the iframe is destroyed
          (function() {
            const originalRemoveChild = Node.prototype.removeChild;
            Node.prototype.removeChild = function(child) {
              try {
                return originalRemoveChild.call(this, child);
              } catch (error) {
                if (error.name === 'NotFoundError' || error.message.includes('removeChild')) {
                  console.warn('Safely ignoring removeChild error:', error.message);
                  return child; // Return the child as if removal succeeded
                }
                throw error; // Re-throw other errors
              }
            };
          })();
        </script>
      `;
    }
    pageContent += `<script src="${storiesCommunityUrl}"></script>
      <script>
        (function waitForStoriesCommunity() {
            if (
              window.StoriesCommunity &&
              typeof window.StoriesCommunity.renderElement === "function"
            ) {
              window.StoriesCommunity.renderElement(
              {
              containers:${JSON.stringify(containers)},
              storyId:"${id}",
              baseUrl:"${storiesCommunityApiUrl}",
              isInPreview:${options.showSocialButtons},
              title:"${title}",
              entityUuid:"${entityUuid ? entityUuid : null}"
              });
            } else {
              // Retry every 200ms until StoriesCommunity is ready
              setTimeout(waitForStoriesCommunity, 200);
            }
      })();
      </script>
      `;
  }
  // tslint:disable max-line-length
  return `
    <!DOCTYPE html>
    <html id="${scopeId}" lang="en">
      <head>
        <meta charset="utf-8">
        <meta http-equiv="X-UA-Compatible" content="IE=edge,chrome=1">
        <title>${title}</title>
        <meta name="description" content="${description}">
        <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no">
        <meta property="og:title" content="${title}">
        <meta property="og:type" content="website">
        <meta property="og:site_name" content="${title}">
        <meta property="og:image" content="${storiesUrl}/${thumbnail_relative_path}">
        <meta property="og:description" content="${description}">
        <meta name="twitter:title" content="${title}">
        <meta name="twitter:description" content="${description}">
        <meta name="twitter:card" content="summary">
        <meta name="twitter:image" content="${storiesUrl}/${thumbnail_relative_path}">
        <meta name="author" content="OpenGov">
        <meta name="robots" content="index, follow">
        ${pageHead}
        <style>
          @media print {
            .powered-by {
              text-align: left;
              background: white;
              padding: 0.5rem 3rem;
              page-break-after: always;
            }
            .powered-by a {
              color: #000;
            }
            .follow-container{
             display:none;
            }  

            .has-media, .flex-with-media, footer, .media-wrapper, .grid, .graph, [class^="container"]{
              page-break-inside: avoid;
            }

            section.contains-media-block .has-media, .table {
              page-break-inside: auto;
            }
            .print-toc {
              page-break-after: always;
            }

            .powered-by a::before {
              content: url("${OPENGOV_LOGO_URL}");
            }

            [class*='col-3'] {
              width: 31.33% !important;
            }

            .three-columns .container {
              display: flex;
              flex-direction: row;
              flex-wrap: wrap;
            }

            .three-columns .container:not(.flex):not(.flex-with-media) {
              .col-3:nth-of-type(2) {
                margin-left: 3%;
                margin-right: 3%;
              }
            }

            .three-columns .container.flex {
              display: flex;
              flex-direction: row;
              flex-wrap: wrap;
              .col-3:nth-of-type(1) {
                order: 1;
              }
              .col-3:nth-of-type(3) {
                order: 2;
              }
              .col-3:nth-of-type(5) {
                order: 3;
              }
              .col-3:nth-of-type(2) {
                order: 4;
              }
              .col-3:nth-of-type(4) {
                order: 5;
              }
              .col-3:nth-of-type(6) {
                order: 6;
              }

              [class*='col-'][class*='col-3']:nth-of-type(2) {
                margin-left: 0;
                margin-right: 0;
              }
              [class*='col-'][class*='col-3']:nth-of-type(3),
              [class*='col-'][class*='col-3']:nth-of-type(4) {
                margin-left: 3%;
                margin-right: 3%;
              }
            }

            .three-columns .container.flex-with-media {
              display: flex;
              flex-direction: row;
              flex-wrap: wrap;
              .col-3:nth-of-type(1) {
                order: 1;
              }
              .col-3:nth-of-type(4) {
                order: 2;
              }
              .col-3:nth-of-type(7) {
                order: 3;
              }
              .col-3:nth-of-type(2) {
                order: 4;
              }
              .col-3:nth-of-type(5) {
                order: 5;
              }
              .col-3:nth-of-type(8) {
                order: 6;
              }
              .col-3:nth-of-type(3) {
                order: 7;
              }
              .col-3:nth-of-type(6) {
                order: 8;
              }
              .col-3:nth-of-type(9) {
                order: 9;
              }

              [class*='col-3']:nth-of-type(2) {
                margin-left: 0;
                margin-right: 0;
              }
              [class*='col-3']:nth-of-type(4),
              [class*='col-3']:nth-of-type(5),
              [class*='col-3']:nth-of-type(6) {
                margin-left: 3%;
                margin-right: 3%;
              }
            }
            }
            /* SYNC: keep in sync with generateBookHtml.ts div.editable print rules */
            @media print {
              div.editable {
                overflow: visible !important;
              }
              div.editable table {
                max-width: 100% !important;
                table-layout: auto;
              }
              div.editable td,
              div.editable th {
                word-wrap: break-word;
              }
            }
            div.editable {
              white-space: normal;
              overflow: hidden;
              overflow-x: auto;
            }
            .align-left { text-align: left; }
            .align-right { text-align: right; }
            .align-center { text-align: center }
            .align-justify { text-align: justify; }
            audio, img, table, video { text-align: inherit; }
            audio.align-left, img.align-left, table.align-left, video.align-left { float: left; margin-right: 12px }
            audio.align-center, img.align-center, table.align-center, video.align-center, audio.align-justify, img.align-justify, table.align-justify, video.align-justify { float: none; display: block; margin: 0 auto; }
            table.align-center, table.align-justify { display: table; }
            audio.align-right, img.align-right, table.align-right, video.align-right { float: right; margin-left: 12px; }
        </style>
      </head>
      <body>
      <div id="community-react-root"></div>
       <div id="${CONTAINERS.FOLLOW_CONTAINER}-${id}" class="${CONTAINERS.FOLLOW_CONTAINER}"></div>
        ${pageContent}
      </body>
    </html>
  `;
}
